import { localStorage, metadata } from 'lib_ui-services';
import lodash from 'lodash';
import asTransactionCode from './asTransactionCode';
import getFormElementHNodes from './getFormElementHNodes';
const { get, set, omit } = lodash;

const PREVIOUS_SELECTIONS_KEY = 'PreviousHNodeSelections';
const PRESELECTED_KEY = 'DefaultHNodeSelections';
// Same key and storage instance `usePersistentState` writes from
// components/formElement/DynamicFieldEntry.js
const DYNAMIC_ENTRIES_KEY = 'dynamicEntries-';
const _p = {
    getDictionary: metadata.getDictionary,
    localStorage
};
export const _private = _p;
/**
 * Finds a DefaultToPreviousSelection marker on either a child filter or a transform.
 * @param {any} hNode
 */
function getDefaultToPreviousSelectionNode(hNode) {
    return (
        metadata.findHNode(hNode.children ?? [], 'DefaultToPreviousSelection') ||
        hNode.transforms?.find(/** @param {any} transform */ transform => transform.hNodeType === 'DefaultToPreviousSelection')
    );
}

/**
 * `DynamicFieldEntry` (Mass Update's "add a value to update") builds its rows at runtime out of the
 * data dictionary and persists them to localForage, so they are NOT in the form's static hNode tree
 * and `getFormElementHNodes` cannot see them. Their values are restored independently by
 * `DefaultToPreviousSelection` straight into component state (useDefaultValueForHNode ->
 * useFormControl's `value` fallback), which means a restored row *displays* a value while the record
 * stays empty -- and the change is then silently dropped at save, because
 * willCreate_namespace_relation_transaction derives `changes` from whatever is left on the record.
 * Pull the persisted rows back here so the DefaultToPreviousSelection pass below covers them too.
 * @param {any[]} hNodes - form elements found in the static hNode tree
 * @returns {Promise<any[]>} the hNodes for rows the user has assigned a field to
 */
async function getDynamicFieldEntryHNodes(hNodes) {
    const dynamicFieldEntries = hNodes.filter(h => h?.hNodeType === 'DynamicFieldEntry' && h.id != null);
    if (dynamicFieldEntries.length === 0) return [];
    const persistedRows = await Promise.all(
        dynamicFieldEntries.map(h => _p.localStorage.getKey(DYNAMIC_ENTRIES_KEY + h.id, undefined, []))
    );
    return persistedRows
        .filter(Array.isArray)
        .flat()
        // A row the user added but never picked a field for is just `{id}` - nothing to restore.
        .filter(row => row?.hNodeType);
}
/**
 * Updates newModel with default values, if necessary.
 * @param {*} context
 * @param {*} newModel
 */
export default async function setDefaultValuesIfNecessary(context, /*byRef*/ newModel) {
    const { hNode, isNew, namespace, relation } = context;

    Object.entries(newModel).forEach(([key, value]) => {
        if (value === '') {
            delete newModel[key];
        }
    });

    if (isNew) {
        // If a model value is undefined, has a corresponding default value and is required
        // then set the model value to the default value.
        let hNodes;
        if (hNode != null) {
            hNodes = getFormElementHNodes(hNode.children);
        } else {
            const dictionary = await _p.getDictionary();
            const values = omit(dictionary[namespace][relation], '_meta');
            hNodes = Object.values(values).map(value => value._meta);
        }
        await Promise.all(
            hNodes
                .filter(h => {
                    if (h.hNodeType === 'InvisibleElement') {
                        return typeof h.defaultValue !== 'undefined' && h.defaultValue !== '';
                    }
                    return (h.required || h.hNodeType === 'Toggle') && typeof h.defaultValue !== 'undefined';
                })
                .map(async hNode => {
                    const path = metadata.getPathToProperty(hNode);
                    const current = get(newModel, path);
                    if (typeof current === 'undefined') {
                        // Nested Reader Mode defaults belong at their full path, not also
                        // at the record root (https://github.com/sstid/backbone/issues/2268).
                        set(newModel, path, hNode.defaultValue);
                    }
                })
        );
        // hNodes is all the form elements of the form, extracted from however deep they were nested.
        // The dynamic rows are appended rather than merged into `hNodes` above on purpose: they must
        // NOT take part in the required/defaultValue pass, which would write a data-dictionary
        // default onto every selected record just because the user left the row blank.
        for (const hNode of [...hNodes, ...(await getDynamicFieldEntryHNodes(hNodes))]) {
            // for each one, we need to check if it has a DefaultToPreviousSelection filter.
            const defaultToPreviousSelection = getDefaultToPreviousSelectionNode(hNode);
            if (defaultToPreviousSelection) {
                // if it does, get the previous value
                const { id } = defaultToPreviousSelection;
                const previousSelections = await _p.localStorage.getKey(PREVIOUS_SELECTIONS_KEY + id);
                // if there was a previous value
                if (previousSelections) {
                    // figure out the full path to the property
                    const path = metadata.getPathToProperty(hNode);
                    // and set it on the newModel
                    set(newModel, path, previousSelections);
                }
            } else {
                // if there was no previous value, check if there is a DefaultValue filter
                const defaultValueFilter = metadata.findHNode(hNode.children ?? [], 'DefaultValue');
                if (defaultValueFilter) {
                    const { id } = defaultValueFilter;
                    const preSelection = await _p.localStorage.getKey(PRESELECTED_KEY + id);
                    // if there is a default value, set a reference to that on the newModel
                    // FUTURE: this only would work with dropdown values. Does that matter?
                    if (preSelection) {
                        const { title, _id } = preSelection;
                        const path = metadata.getPathToProperty(hNode);
                        set(newModel, path, { title, _id });
                    }
                }
            }
        }

        if (!newModel.title && namespace === 'item' && relation === 'transaction') {
            // "transactions" by their very nature don't have a title.
            // But everything in backbone assumes one. So, duplicate the _id as title.
            newModel.title = asTransactionCode(newModel._id);
        }
    }
}
